import type { DecisionQuestion, Scenario } from '../../types/prioritization-selector';

export const scenarios: Scenario[] = [
  {
    id: 'task',
    name: 'Task Prioritization',
    description: 'Prioritizing individual tasks or small work items',
    context: 'Personal or small team task management',
    startingQuestion: 'Q1'
  },
  {
    id: 'initiative',
    name: 'Initiative Planning',
    description: 'Planning projects or initiatives with some uncertainty',
    context: 'Project or initiative planning with moderate complexity',
    startingQuestion: 'Q1'
  },
  {
    id: 'portfolio',
    name: 'Portfolio Management',
    description: 'Managing multiple projects or initiatives across teams',
    context: 'Strategic portfolio and multi-team coordination',
    startingQuestion: 'Q1'
  }
];

export const decisionQuestions: DecisionQuestion[] = [
  {
    id: 'Q1',
    text: 'What size of thing are you prioritizing?',
    type: 'single-choice',
    required: true,
    helpText: 'Choose the scope of work you need to prioritize',
    options: [
      {
        id: 'Q1-task',
        text: 'Task - A single small piece of work',
        value: 'task',
        nextQuestion: 'Q2A'
      },
      {
        id: 'Q1-initiative',
        text: 'Initiative/Project - A larger, multi-step effort',
        value: 'initiative',
        nextQuestion: 'Q2B'
      },
      {
        id: 'Q1-portfolio',
        text: 'Portfolio/Program - Multiple big efforts across teams',
        value: 'portfolio',
        nextQuestion: 'Q2C'
      }
    ]
  },
  {
    id: 'Q2A',
    text: 'Are you working alone or with others?',
    type: 'single-choice',
    required: true,
    parentQuestion: 'Q1',
    condition: 'task',
    helpText: 'This determines the level of collaboration needed',
    options: [
      {
        id: 'Q2A-solo',
        text: 'Alone - Just me',
        value: 'solo',
        nextQuestion: 'Q3A'
      },
      {
        id: 'Q2A-team',
        text: 'With others - Team collaboration',
        value: 'team',
        nextQuestion: 'Q3A'
      }
    ]
  },
  {
    id: 'Q2B',
    text: 'Is the work high in uncertainty?',
    type: 'single-choice',
    required: true,
    parentQuestion: 'Q1',
    condition: 'initiative',
    helpText: 'High uncertainty means many unknowns, unclear requirements, or experimental work',
    options: [
      {
        id: 'Q2B-yes',
        text: 'Yes - High uncertainty, many unknowns',
        value: 'high_uncertainty',
        nextQuestion: 'Q3B'
      },
      {
        id: 'Q2B-no',
        text: 'No - Low to medium uncertainty',
        value: 'low_med_uncertainty',
        nextQuestion: 'Q3C'
      }
    ]
  },
  {
    id: 'Q2C',
    text: 'Does the work involve dependencies across teams?',
    type: 'single-choice',
    required: true,
    parentQuestion: 'Q1',
    condition: 'portfolio',
    helpText: 'Cross-team dependencies require coordination between different teams',
    options: [
      {
        id: 'Q2C-yes',
        text: 'Yes - Multiple teams need to coordinate',
        value: 'cross_team_deps_yes',
        nextQuestion: 'Q4'
      },
      {
        id: 'Q2C-no',
        text: 'No - Limited cross-team coordination needed',
        value: 'cross_team_deps_no',
        nextQuestion: 'Q4'
      }
    ]
  },
  {
    id: 'Q3A',
    text: 'How many items are you deciding between?',
    type: 'single-choice',
    required: true,
    parentQuestion: 'Q2A',
    helpText: 'The number of items affects the complexity of the prioritization process',
    options: [
      {
        id: 'Q3A-few',
        text: '3-7 items - Small set',
        value: 'items_3_7',
        nextQuestion: 'Q4'
      },
      {
        id: 'Q3A-many',
        text: 'Many items - Large set',
        value: 'items_many',
        nextQuestion: 'Q4'
      }
    ]
  },
  {
    id: 'Q3B',
    text: 'Are there high dependencies?',
    type: 'single-choice',
    required: true,
    parentQuestion: 'Q2B',
    condition: 'high_uncertainty',
    helpText: 'Dependencies mean items rely on each other or have integration points',
    options: [
      {
        id: 'Q3B-yes',
        text: 'Yes - Many dependencies',
        value: 'deps_high',
        nextQuestion: 'Q4'
      },
      {
        id: 'Q3B-no',
        text: 'No - Limited dependencies',
        value: 'deps_low',
        nextQuestion: 'Q4'
      }
    ]
  },
  {
    id: 'Q3C',
    text: 'Is this a one-way door decision?',
    type: 'single-choice',
    required: true,
    parentQuestion: 'Q2B',
    condition: 'low_med_uncertainty',
    helpText: 'One-way door decisions are hard or costly to reverse once made',
    options: [
      {
        id: 'Q3C-yes',
        text: 'Yes - Hard to reverse, high stakes',
        value: 'one_way_yes',
        nextQuestion: 'Q4'
      },
      {
        id: 'Q3C-no',
        text: 'No - Can be adjusted or reversed',
        value: 'one_way_no',
        nextQuestion: 'Q4'
      }
    ]
  },
  {
    id: 'Q4',
    text: 'Are you prioritizing for yourself or for someone else to execute?',
    type: 'single-choice',
    required: true,
    helpText: 'This determines if you need delegation or cross-team alignment',
    options: [
      {
        id: 'Q4-others',
        text: 'For others - I\'m setting priorities for someone else',
        value: 'others',
        nextQuestion: 'Q5'
      },
      {
        id: 'Q4-self',
        text: 'For myself - Personal prioritization',
        value: 'self',
        nextQuestion: 'Q5'
      }
    ]
  },
  {
    id: 'Q5',
    text: 'Is it urgent, and is it hard to reverse once started?',
    type: 'single-choice',
    required: true,
    helpText: 'High-stakes decisions have significant consequences and are costly to change',
    options: [
      {
        id: 'Q5-yes',
        text: 'Yes - Urgent and hard to reverse',
        value: 'urgent_hard_yes',
        nextQuestion: 'Q6'
      },
      {
        id: 'Q5-no',
        text: 'No - Can be adjusted if needed',
        value: 'urgent_hard_no',
        nextQuestion: 'Q6'
      }
    ]
  },
  {
    id: 'Q6',
    text: 'How many total items are you deciding between?',
    type: 'single-choice',
    required: true,
    helpText: 'The volume of items determines the complexity of the prioritization method',
    options: [
      {
        id: 'Q6-few',
        text: '≤10 items — small set',
        value: '≤10',
        nextQuestion: 'Qa',
      },
      {
        id: 'Q6-medium',
        text: '11–50 items — medium set',
        value: '11-50',
        nextQuestion: 'Qa',
      },
      {
        id: 'Q6-many',
        text: '>50 items — large set',
        value: '>50',
        nextQuestion: 'Qa',
      },
    ]
  },
  {
    id: 'Qa',
    text: 'How much time do you have for this prioritization process?',
    type: 'single-choice',
    required: true,
    helpText: 'Include prep, facilitation, and decision time — not just the meeting itself',
    options: [
      {
        id: 'Qa-quick',
        text: 'Under 30 minutes — quick session, low ceremony',
        value: 'time_quick',
        nextQuestion: 'Qb',
      },
      {
        id: 'Qa-hours',
        text: 'A few hours — dedicated workshop or planning block',
        value: 'time_hours',
        nextQuestion: 'Qb',
      },
      {
        id: 'Qa-days',
        text: 'Multiple days — full planning cycle or quarterly review',
        value: 'time_days',
        nextQuestion: 'Qb',
      },
    ]
  },
  {
    id: 'Qb',
    text: 'How does your team prefer to score or compare items?',
    type: 'single-choice',
    required: true,
    helpText: 'A simpler method used well beats a complex one used poorly — be honest about comfort level',
    options: [
      {
        id: 'Qb-visual',
        text: 'Visual & intuitive — placement, feel, or forced-ranking; no formulas',
        value: 'scoring_visual',
        // terminal — no nextQuestion
      },
      {
        id: 'Qb-light',
        text: 'Light scoring — simple criteria like High/Med/Low or 1–5 scales',
        value: 'scoring_light',
      },
      {
        id: 'Qb-quantitative',
        text: 'Quantitative — we have data and want a numeric formula (RICE, WSJF, Cost of Delay)',
        value: 'scoring_quantitative',
      },
    ]
  },
];

export const getQuestionById = (id: string): DecisionQuestion | undefined => {
  return decisionQuestions.find(question => question.id === id);
};

export const getNextQuestion = (questionId: string, answer: string): DecisionQuestion | null => {
  const question = getQuestionById(questionId);
  if (!question) return null;

  const option = question.options?.find(opt => opt.value === answer);
  if (!option || !option.nextQuestion) return null;

  return getQuestionById(option.nextQuestion) || null;
};

export const getBundleFromAnswer = (questionId: string, answer: string): string | null => {
  const question = getQuestionById(questionId);
  if (!question) return null;

  const option = question.options?.find(opt => opt.value === answer);
  return option?.bundle || null;
};

export const getAddOnsFromAnswer = (questionId: string, answer: string): string[] => {
  const question = getQuestionById(questionId);
  if (!question) return [];

  const option = question.options?.find(opt => opt.value === answer);
  return option?.addOns || [];
};

